import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function DocumentTypesCard() {
  const [rows, setRows] = useState(null);
  const [name, setName] = useState("");
  const [track, setTrack] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/document-types").then((r) => setRows(r.data)).catch(() => toast.error("Could not load document types")), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) { toast.error("Enter a name for the document type"); return; }
    setSaving(true);
    try { await api.post("/document-types", { name: name.trim(), track_expiry: track }); toast.success("Document type added"); setName(""); setTrack(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const save = async (r) => {
    try { await api.put(`/document-types/${r.id}`, { name: (r.name || "").trim(), track_expiry: !!r.track_expiry }); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    try { await api.delete(`/document-types/${r.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setRow = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));

  if (!rows) return <div className="text-slate-400 text-sm" data-testid="doctypes-loading">Loading document types…</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5 max-w-3xl" data-testid="document-types-card">
      <div>
        <h3 className="font-semibold text-[#0B1A2F]">Document types</h3>
        <p className="text-xs text-slate-400 mt-1">Categories used when uploading documents to an employee record. Turn on <strong>Track expiry</strong> for types that lapse (e.g. Right to work, Visa) — staff are prompted for an expiry date at upload and HR is reminded before they expire.</p>
      </div>

      <div className="divide-y divide-slate-100">
        {rows.length === 0 && <p className="py-3 text-slate-400 text-sm">No document types yet.</p>}
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`doctype-row-${r.id}`}>
            <Input className="h-9 flex-1" value={r.name || ""} onChange={(e) => setRow(r.id, "name", e.target.value)} data-testid={`doctype-name-${r.id}`} />
            <label className="flex items-center gap-2 text-xs text-slate-600 shrink-0">
              <Switch checked={!!r.track_expiry} onCheckedChange={(v) => setRow(r.id, "track_expiry", v)} data-testid={`doctype-track-${r.id}`} />
              Track expiry
            </label>
            <Button size="sm" variant="outline" className="h-9" onClick={() => save(r)} data-testid={`doctype-save-${r.id}`}><Save size={14} /></Button>
            <Button size="sm" variant="ghost" className="h-9 text-rose-600" onClick={() => del(r)} data-testid={`doctype-del-${r.id}`}><Trash2 size={14} /></Button>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-100 pt-4">
        <h4 className="text-sm font-semibold text-[#0B1A2F] mb-3">Add a document type</h4>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[12rem]"><Label className="text-xs text-slate-500 block mb-1">Name</Label><Input className="h-9" placeholder="e.g. Visa" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="doctype-new-name" /></div>
          <label className="flex items-center gap-2 text-xs text-slate-600 h-9"><Switch checked={track} onCheckedChange={setTrack} data-testid="doctype-new-track" />Track expiry</label>
          <Button onClick={add} disabled={saving} className="bg-[#2EA3FD] hover:bg-[#1B8EE6] h-9" data-testid="doctype-add-btn"><Plus size={15} className="mr-1" />Add</Button>
        </div>
      </div>
    </div>
  );
}
